import { AsoScenarioResponse, AsoScenarioStatus } from '@ydistri/api-sdk';
import { useMemo } from 'react';
import { useStableParams } from '../../../routes/useStableParams';
import { useGetAsoScenarioListQuery } from '../apiAso';

const activeStatuses = [
  AsoScenarioStatus.Pending,
  AsoScenarioStatus.Queued,
  AsoScenarioStatus.Running,
];

const editableStatuses = [
  AsoScenarioStatus.Completed,
  AsoScenarioStatus.Crashed,
  AsoScenarioStatus.Outdated,
];

export interface AsoScenarioStatusInfo {
  scenario?: AsoScenarioResponse;
  status?: AsoScenarioStatus;
  isLocked: boolean;
  isEditable: boolean;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  message?: string;
}

export const isAsoScenarioLocked = (status?: AsoScenarioStatus): boolean =>
  status !== undefined && activeStatuses.includes(status);

export const isAsoScenarioEditable = (status?: AsoScenarioStatus): boolean =>
  status !== undefined && editableStatuses.includes(status);

export const asoScenarioStatusToMessage = (status?: AsoScenarioStatus): string | undefined => {
  switch (status) {
    case undefined:
      return undefined;
    case AsoScenarioStatus.Pending:
    case AsoScenarioStatus.Queued:
      return 'Computation will start soon';
    case AsoScenarioStatus.Running:
      return 'Computation is running';
    case AsoScenarioStatus.Completed:
    case AsoScenarioStatus.Crashed:
    case AsoScenarioStatus.Outdated:
    default:
      return undefined;
  }
};

export const useAsoScenarioStatus = (): AsoScenarioStatusInfo => {
  const { asoScenarioId } = useStableParams();
  const { currentScenario, isLoading, isFetching, isError } = useGetAsoScenarioListQuery(
    undefined,
    {
      skip: !asoScenarioId,
      selectFromResult: ({ data, isError, isFetching, isLoading }) => ({
        currentScenario: data?.find(scenario => scenario.id === asoScenarioId),
        isError,
        isFetching,
        isLoading,
      }),
    },
  );

  return useMemo(() => {
    const status = currentScenario?.status;

    return {
      scenario: currentScenario,
      status,
      isLocked: isAsoScenarioLocked(status),
      isEditable: isAsoScenarioEditable(status),
      isLoading,
      isFetching,
      isError,
      message: asoScenarioStatusToMessage(status),
    };
  }, [currentScenario, isError, isFetching, isLoading]);
};
